import { FC } from 'react'
import { EmergencyContactFragment } from '../../../types'
import PerText from '../../../components/custom_components/Text'
import { Table } from 'antd'
import { useTranslation } from 'react-i18next'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { SortOrder } from 'antd/lib/table'
import { PerTag } from '../../../components/custom_components/Tag'
import { isColumnEmpty } from '../utils'

interface EmergencyContactsTableProps {
  emergencyContacts: EmergencyContactFragment[]
}

export const EmergencyContactsTable: FC<EmergencyContactsTableProps> = ({ emergencyContacts }) => {
  const { t } = useTranslation()
  const dataSource = emergencyContacts.map((emergencyContact) => ({ ...emergencyContact, key: emergencyContact.id }))

  const tagColor = (type: string) => {
    if (type === 'PRIMARY') {
      return 'cyan'
    }
    if (type === 'SECONDARY') {
      return undefined
    }
    return undefined // fallback
  }

  const contactsColumns = [
    {
      key: 'emergencyContact-name',
      title: t('profile.emergencyContacts.name'),
      className: 'minWidth-column-xxl',
      dataIndex: 'firstName',
      render: (dataIndex, row) => <PerText>{dataIndex + ' ' + row.lastName}</PerText>,
    },
    {
      key: 'emergencyContact-emailAddress',
      title: t('profile.emergencyContacts.emailAddress'),
      className: 'minWidth-column-xxl',
      dataIndex: 'emailAddress',
      render: (dataIndex) => <PerText>{dataIndex}</PerText>,
    },
    {
      key: 'emergencyContact-phoneNumber',
      title: t('profile.emergencyContacts.phoneNumber'),
      className: 'minWidth-column-xxl',
      dataIndex: 'phoneNumber',
      render: (dataIndex) => <PerText>{dataIndex}</PerText>,
    },
    {
      key: 'emergencyContact-contactType',
      title: t('profile.emergencyContacts.type'),
      dataIndex: 'contactType',
      className: 'minWidth-column-m',
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (typeA, typeB) => {
        const a = typeA.contactType ? typeA.contactType : ' '
        const b = typeB.contactType ? typeB.contactType : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex) => <PerTag color={tagColor(dataIndex)}>{t(`employee.emergencyContacts.type.${dataIndex}`)}</PerTag>,
    },
  ]

  // get non empty columns
  const filteredColumns = contactsColumns.filter((column) => !isColumnEmpty(column, dataSource))
  // get empty columns to use it as padding at the end of a row
  const paddingColumns = contactsColumns
    .filter((column) => isColumnEmpty(column, dataSource))
    .map((col) => {
      col.dataIndex = ''
      col.title = ''
      col.sorter = undefined
      col.render = () => <></>
      return col
    })

  return (
    <>
      <PerText appearance="tile-heading" mb="s">
        {t('profile.emergencyContacts.header')}
      </PerText>
      <Table
        className="small"
        rowClassName={() => 'minHeight-row'}
        dataSource={dataSource}
        columns={filteredColumns.concat(paddingColumns)}
        pagination={false}
        locale={{
          emptyText: <PerEmpty info={t('profile.emergencyContacts.emptyTable')} />,
        }}
      />
    </>
  )
}
